import React from 'react';
import { t, Trans } from '@lingui/macro';

import { Button, Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import { SeoLocationDetails } from '@rover/rsdk/src/apiClient/latest/model/seoLocationDetails';
import { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import getSearchUrl from '../../../../utils/getSearchUrl';
import ReviewsCarousel from '../../components/ReviewsCarousel';
import {
  ReviewsCarouselPaginateAction,
  ReviewsCtaClick,
  ViewReviewCard,
} from '../../utilities/analyticsEvents';
import { PageSectionId, useSectionRef } from '../hooks/useSectionRef';

function getHeading(serviceTypeSlug: ServiceTypeSlugEnum, locationName: string): string {
  switch (serviceTypeSlug) {
    case ServiceTypeSlugEnum['dog-boarding']:
    case ServiceTypeSlugEnum['doggy-day-care']:
    case ServiceTypeSlugEnum['dog-walking']:
    case ServiceTypeSlugEnum['dog-sitting']:
    case ServiceTypeSlugEnum['dog-training']:
      return t`${locationName} dog parents share their Rover experiences`;

    case ServiceTypeSlugEnum['cat-boarding']:
    case ServiceTypeSlugEnum['cat-sitting']:
      return t`${locationName} cat parents share their Rover experiences`;

    case ServiceTypeSlugEnum['drop-in-visits']:
    case ServiceTypeSlugEnum['house-sitting']:
    case ServiceTypeSlugEnum['pet-sitting']:
    case ServiceTypeSlugEnum['pet-boarding']:
    default:
      return t`${locationName} pet parents share their Rover experiences`;
  }
}

type Props = {
  seoLocation: { details: Pick<SeoLocationDetails, 'name' | 'serviceTypeSlug'> };
  language: string;
  searchFilters: Partial<SearchFilters>;
  header?: string;
} & Pick<React.ComponentProps<typeof ReviewsCarousel>, 'reviews'>;

const SeoLocationReviewsCarousel: React.FC<Props> = ({
  seoLocation: { details },
  reviews,
  language,
  searchFilters,
  header,
}) => {
  const ref = useSectionRef(PageSectionId.ReviewsCarousel);

  if (!reviews.length) return null;

  return (
    <Section htmlRef={ref} paddingLeft="4x" mb={['16x', '12x']}>
      <Flex gap="6x" flexDirection="column" alignItems="center">
        <ReviewsCarousel
          heading={header ?? getHeading(details.serviceTypeSlug, details.name)}
          headingVariant="display"
          reviews={reviews}
          onCardViewed={(index) => emitAnalyticsEvent(new ViewReviewCard({ index }))}
          cardProps={{
            href: getSearchUrl(searchFilters, language),
            hrefLabel: t`Find pet care near you`,
          }}
          onPaginate={(paginateParams) =>
            emitAnalyticsEvent(
              new ReviewsCarouselPaginateAction({
                serviceTypeSlug: details.serviceTypeSlug,
                ...paginateParams,
              })
            )
          }
        />
        <Button
          variant="primary"
          width="100%"
          maxWidth={['335px', '375px']}
          href={getSearchUrl(searchFilters, language)}
          onPress={() => {
            new ReviewsCtaClick({
              serviceTypeSlug: details.serviceTypeSlug,
            }).fireCrossPageEvent();
          }}
        >
          <Trans>Find pet care near you</Trans>
        </Button>
      </Flex>
    </Section>
  );
};

export default SeoLocationReviewsCarousel;
